import React, { useState } from "react";
import {
  Typography,
  Button,
  Stack,
  Box,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AuthCard from "../../components/layout/AuthCard";
import { useLocation } from "react-router-dom";
import { useGetRegistrationDataQuery } from "../../redux/api/authApi";
import ReqMark from "./ReqMark";

export default function Step5SecondfileSubmit({
  nextStep,
  prevStep,
  defaultValues = {},
}) {
  // State for this step
  const [primaryAssetType, setPrimaryAssetType] = useState(
    defaultValues.primaryAssetType || ""
  );
  const [assetsOwned, setAssetsOwned] = useState(
    defaultValues.assetsOwned || ""
  );
  const [errors, setErrors] = useState({});

  // Registration config data
  const { data: registrationData } = useGetRegistrationDataQuery();

  // After registrationData is available
  const primaryAssetTypeOptions = React.useMemo(() => {
    const obj = registrationData?.data?.primaryAssetTypeOptions || {};
    return Object.entries(obj).map(([key, label]) => ({
      key, // REAL_ESTATE
      value: label, // Real Estate  (this is what you'll store & send)
      label,
    }));
  }, [registrationData]);

  // assetsOwnedOptions already comes as an array
  const assetsOwnedOptions = registrationData?.data?.assetsOwnedOptions || [];

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const role = queryParams.get("role");

  const handleNextClick = () => {
    const newErrors = {};
    if (!primaryAssetType) newErrors.primaryAssetType = "Please select a primary asset type.";
    if (!assetsOwned) newErrors.assetsOwned = "Please select how many assets you own.";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    if (typeof nextStep === "function") {
      nextStep({ primaryAssetType, assetsOwned });
    }
  };

  return (
    <AuthCard>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="h6" fontWeight={700}>
          Create Account
        </Typography>
        <Button
          variant="outlined"
          size="small"
          sx={{
            color: "#38bdf8",
            borderColor: "rgba(56,189,248,0.4)",
            borderRadius: "999px",
            textTransform: "none",
            px: 2,
            height: 28,
            fontSize: 12,
            "&:hover": { background: "rgba(56,189,248,0.1)" },
          }}
        >
          {role === "investor"
            ? "Investor"
            : role === "assetOwner"
            ? "Asset Owner"
            : "Admin"}
        </Button>
      </Stack>

      <Typography color="text.secondary" sx={{ mb: 0.5 }}>
        Step 4 of 5 - Asset Information
      </Typography>
      <Typography variant="caption" sx={{ display: "block", color: "#f87171", mb: 2 }}>
        * All fields in this step are required.
      </Typography>

      {/* Segmented Progress Bar */}
      <Stack direction="row" spacing={1} sx={{ mb: 4 }}>
        {[1, 2, 3, 4, 5].map((bar, index) => (
          <Box
            key={bar}
            sx={{
              flex: 1,
              height: 3,
              borderRadius: 1,
              backgroundColor: index < 4 ? "#0ea5e9" : "rgba(255,255,255,0.1)", // 4th segment active
            }}
          />
        ))}
      </Stack>

      {/* Form Fields */}
      <Stack spacing={2}>
        {/* Primary Asset Type */}

        <Box>
          <Typography
            variant="body2"
            sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}
          >
            Primary Asset Type
            <ReqMark />
          </Typography>

          <FormControl fullWidth error={!!errors.primaryAssetType}>
            <Select
              value={primaryAssetType}
              onChange={(e) => { setPrimaryAssetType(e.target.value); setErrors((p) => ({ ...p, primaryAssetType: "" })); }}
              displayEmpty
              sx={{ backgroundColor: "rgba(255,255,255,0.05)", borderRadius: "10px" }}
              inputProps={{ "aria-label": "Primary Asset Type" }}
            >
              <MenuItem value="">
                <Typography variant="body2" color="text.secondary">
                  Select primary asset type
                </Typography>
              </MenuItem>

              {primaryAssetTypeOptions.map((option) => (
                <MenuItem key={option.key} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {errors.primaryAssetType && <FormHelperText>{errors.primaryAssetType}</FormHelperText>}
          </FormControl>
        </Box>

        {/* How many assets do you own? */}
        <Box>
          <Typography
            variant="body2"
            sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}
          >
            How many assets do you own?
            <ReqMark />
          </Typography>
          <FormControl fullWidth error={!!errors.assetsOwned}>
            <Select
              value={assetsOwned}
              onChange={(e) => { setAssetsOwned(e.target.value); setErrors((p) => ({ ...p, assetsOwned: "" })); }}
              displayEmpty
              sx={{ backgroundColor: "rgba(255,255,255,0.05)", borderRadius: "10px" }}
              inputProps={{ "aria-label": "Assets Owned Range" }}
            >
              <MenuItem value="">
                <Typography variant="body2" color="text.secondary">
                  Select range
                </Typography>
              </MenuItem>
              {assetsOwnedOptions.map((option) => (
                <MenuItem key={option.key} value={option.key}>
                  {option.key} {option.label}
                </MenuItem>
              ))}
            </Select>
            {errors.assetsOwned && <FormHelperText>{errors.assetsOwned}</FormHelperText>}
          </FormControl>
        </Box>

        {/* Info Box */}
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: "12px",
            backgroundColor: "rgba(15,23,42,0.9)",
            border: "1px solid rgba(148,163,184,0.35)",
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, mb: 0.5, color: "#e2e8f0" }}
          >
            Asset Listing
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You&apos;ll be able to list and tokenize your assets after account
            verification.
          </Typography>
        </Box>
      </Stack>

      {/* Actions */}
      <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          fullWidth
          sx={{
            borderRadius: "12px",
            textTransform: "none",
            borderColor: "rgba(148,163,184,0.4)",
            color: "#e2e8f0",
            backgroundColor: "rgba(15,23,42,0.9)",
            "&:hover": {
              backgroundColor: "rgba(15,23,42,1)",
            },
          }}
          onClick={prevStep}
        >
          Back
        </Button>
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          fullWidth

          sx={{
            background: "linear-gradient(90deg,#0284c7,#0ea5e9)",
            borderRadius: "12px",
            textTransform: "none",
          }}
          onClick={handleNextClick}
        >
          Continue
        </Button>
      </Stack>
    </AuthCard>
  );
}
